import { Component, effect, inject, input, output, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { initialsFromName } from '../avatar/ui-avatar.component';
import { UiSkeletonComponent } from '../skeleton/ui-skeleton.component';

@Component({
  selector: 'ui-side-nav-brand',
  standalone: true,
  imports: [RouterLink, UiSkeletonComponent],
  template: `
    <a
      routerLink="/dashboard"
      class="ui-side-nav-brand"
      [class.ui-side-nav-brand--drawer]="drawer()"
      [class.ui-side-nav-brand--collapsed]="collapsed()"
      [attr.aria-label]="'Ir para o início — ' + (name() || 'Veitros')"
      (click)="navigated.emit()"
    >
      @if (loading()) {
        <ui-skeleton variant="brand" />
      } @else {
        <div class="ui-side-nav-brand__card">
          @if (showLogo()) {
            <div class="ui-side-nav-brand__logo-frame">
              <img
                [src]="logoUrl()!"
                [alt]="name() || 'Logo da associação'"
                class="ui-side-nav-brand__logo"
                (error)="onLogoError()"
              />
            </div>
          } @else {
            <div class="ui-side-nav-brand__logo-fallback" aria-hidden="true">{{ initials }}</div>
          }
          <div class="ui-side-nav-brand__text">
            <span class="ui-side-nav-brand__name">{{ name() || 'Veitros' }}</span>
            <span class="ui-side-nav-brand__tag">{{ tagline() }}</span>
          </div>
        </div>
      }
    </a>
  `,
  styles: [`
    :host {
      display: block;
      flex-shrink: 0;
    }

    .ui-side-nav-brand {
      display: block;
      padding: 0.875rem 0.75rem 0.75rem;
      text-decoration: none;
      color: inherit;
      border-bottom: 1px solid var(--border-color);
      transition: background 0.15s;
    }

    .ui-side-nav-brand--drawer {
      padding-inline: 0.5rem;
    }

    .ui-side-nav-brand--collapsed {
      padding-inline: 0.5rem;
    }

    .ui-side-nav-brand--collapsed .ui-side-nav-brand__card {
      justify-content: center;
      padding: 0.5rem;
    }

    .ui-side-nav-brand--collapsed .ui-side-nav-brand__text {
      display: none;
    }

    .ui-side-nav-brand--collapsed .ui-side-nav-brand__logo-frame,
    .ui-side-nav-brand--collapsed .ui-side-nav-brand__logo-fallback {
      width: 2.5rem;
      height: 2.5rem;
    }

    .ui-side-nav-brand:hover {
      background: color-mix(in srgb, var(--brand-primary) 4%, var(--surface-bg, #fff));
    }

    .ui-side-nav-brand:focus-visible {
      outline: none;
      box-shadow: inset 0 0 0 2px color-mix(in srgb, var(--brand-primary) 35%, transparent);
    }

    .ui-side-nav-brand__card {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      min-width: 0;
      padding: 0.625rem 0.75rem;
      border-radius: 0.75rem;
      border: 1px solid color-mix(in srgb, var(--border-color) 85%, transparent);
      background: linear-gradient(
        135deg,
        var(--surface-bg, #fff) 0%,
        color-mix(in srgb, var(--brand-primary) 5%, var(--surface-bg, #fff)) 55%,
        color-mix(in srgb, var(--brand-secondary, #52c41a) 6%, var(--surface-bg, #fff)) 100%
      );
      box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
    }

    .ui-side-nav-brand__logo-frame {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      width: 3rem;
      height: 3rem;
      padding: 0.35rem;
      border-radius: 0.625rem;
      background: var(--surface-bg, #fff);
      border: 1px solid color-mix(in srgb, var(--border-color) 70%, transparent);
      box-shadow: inset 0 1px 0 color-mix(in srgb, var(--surface-bg, #fff) 90%, transparent);
    }

    .ui-side-nav-brand__logo {
      display: block;
      max-width: 100%;
      max-height: 100%;
      width: auto;
      height: auto;
      object-fit: contain;
    }

    .ui-side-nav-brand__logo-fallback {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      width: 3rem;
      height: 3rem;
      border-radius: 0.625rem;
      background: color-mix(in srgb, var(--brand-primary) 14%, var(--surface-bg, #fff));
      color: var(--brand-primary, #1677ff);
      font-size: 0.875rem;
      font-weight: 800;
      letter-spacing: 0.02em;
      border: 1px solid color-mix(in srgb, var(--brand-primary) 22%, transparent);
    }

    .ui-side-nav-brand__text {
      display: flex;
      flex-direction: column;
      gap: 0.125rem;
      min-width: 0;
      flex: 1;
    }

    .ui-side-nav-brand__name {
      font-size: 0.8125rem;
      font-weight: 700;
      line-height: 1.25;
      color: var(--text-primary, #0f172a);
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }

    .ui-side-nav-brand__tag {
      font-size: 0.6875rem;
      font-weight: 500;
      color: var(--text-muted, #64748b);
      line-height: 1.2;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

  `],
})
export class UiSideNavBrandComponent {
  name = input('');
  logoUrl = input<string | null>(null);
  loading = input(false);
  tagline = input('Painel de gestão');
  drawer = input(false);
  collapsed = input(false);

  navigated = output<void>();

  readonly logoBroken = signal(false);

  constructor() {
    effect(() => {
      this.logoUrl();
      this.logoBroken.set(false);
    });
  }

  get initials(): string {
    return initialsFromName(this.name() || 'Veitros');
  }

  showLogo(): boolean {
    return !!this.logoUrl() && !this.logoBroken();
  }

  onLogoError(): void {
    this.logoBroken.set(true);
  }
}
